import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { Colors } from 'src/shared/components/styles/colors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberfeesByMemberIds } from 'src/shared/services/memberfeeService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { MemberFeeTypeEnum } from 'src/shared/models/enums/memberFeeType.enum';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useFormContext } from 'react-hook-form';
import { Member } from '../models/member';
import { MemberCreateInvoiceTemplate } from './memberCreateInvoiceTemplate';
import { MemberFee } from '../models/memberFee';

type MemberCreateInvoiceTableSumProps = {
  members: Member[];
  ownerId: number | undefined;
  invoiceTemplateIdChanged: (invoiceTemplateId: number | undefined) => void;
  setTotaltAmount?: (totaltAmount: number) => void;
};

export const MemberCreateInvoiceTableSum = (props: MemberCreateInvoiceTableSumProps) => {
  const { members, ownerId, invoiceTemplateIdChanged, setTotaltAmount } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['invoice']);
  const { getValues, setValue } = useFormContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [invoiceTemplateId, setInvoiceTemplateId] = useState<number | undefined>();
  const [includeFamilyMembers, setincludeFamilyMembers] = useState<boolean>(true);
  const [memberFees, setMemberFees] = useState<MemberFee[]>([]);

  const isMemberMainMember = members.some((member) => member.isMainMember === true);
  const isMemberFamilyMember = members.some((member) => member.isFamilyMember === true);

  useEffect(() => {
    if (!loading && memberFees.length > 0 && setTotaltAmount) {
      const totalAmount = memberFees.map((x) => x.amount).reduce((a, b) => a + b, 0);
      setTotaltAmount(totalAmount);
    } else if (!loading && setTotaltAmount) {
      setTotaltAmount(0);
    }
  }, [loading, memberFees, setTotaltAmount]);

  useEffect(() => {
    const setMembers = memberFees.map((member) => member.memberId);
    const currentMemberIds = getValues('memberIds');

    if (currentMemberIds.length !== setMembers.length || !currentMemberIds.every((id, index) => id === setMembers[index])) {
      setValue('memberIds', setMembers);
    }
  }, [memberFees, setValue, getValues]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getMemberfeesByMemberIds(
          0,
          500,
          'memberId',
          'ASC',
          members.map((x) => x.member_id),
          invoiceTemplateId,
          includeFamilyMembers,
        );
        if (isMounted()) setMemberFees(result.data.results);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    if (invoiceTemplateId) {
      fetchData();
    }
  }, [includeFamilyMembers, invoiceTemplateId, isMounted, members]);

  const changeInvoiceTemplateId = (x) => {
    invoiceTemplateIdChanged(x);
    setInvoiceTemplateId(x);
  };

  const handleChange = (event) => {
    setincludeFamilyMembers(event.target.value === 'true');
  };

  return (
    <>
      <Grid item xs={4}>
        <ControlledDatePicker name='dueDate' label={t('invoice:createInvoice.dueDate')} />
      </Grid>
      <Grid item xs={8}>
        <MemberCreateInvoiceTemplate ownerId={ownerId} callback={(x) => changeInvoiceTemplateId(x)} />
      </Grid>
      {members.length === 1 && invoiceTemplateId && (
        <Grid item xs={12}>
          {isMemberMainMember && (
            <FormControl>
              <FormLabel id='includeFamilyMembers'>{t('invoice:createInvoice.includeFamilyMembers')}</FormLabel>
              <RadioGroup aria-labelledby='includeFamilyMembers' name='includeFamilyMembers' value={includeFamilyMembers} row onChange={handleChange}>
                <FormControlLabel value control={<Radio />} label={t('common:button.yes')} />
                <FormControlLabel value={false} control={<Radio />} label={t('common:button.no')} />
              </RadioGroup>
            </FormControl>
          )}
          {isMemberFamilyMember && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('invoice:createInvoice.familyMemberWarningText')}
            </Alert>
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        {!invoiceTemplateId && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {t('invoice:createInvoice.selectInvoiceTemplateWarning')}
          </Alert>
        )}
        {invoiceTemplateId && (
          <>
            <Typography gutterBottom variant='h6'>
              {t('invoice:createInvoice.tableSumHeader')}
            </Typography>
            <Divider />
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', p: 6, flexDirection: 'column' }}>
                <CircularProgress size={50} sx={{ color: `${Colors.Menu} !important`, mb: 3 }} thickness={2.5} />
                <Typography sx={{ color: `${Colors.Menu} !important`, fontWeight: 500 }} variant='h6'>
                  {t('invoice:createInvoice.loadingSumTable')}
                </Typography>
              </Box>
            )}
            {!loading && memberFees.length > 0 && (
              <TableContainer
                sx={{
                  mb: 2,
                  '& .head .MuiTableRow-head th': { borderBottom: '2px solid rgba(224, 224, 224, 1)' },
                  '& .footer .MuiTableRow-head th': { borderBottom: 'none' },
                  '& tbody td': { whiteSpace: 'nowrap' },
                }}
              >
                <Table size='small'>
                  <TableHead className='head'>
                    <TableRow>
                      <TableCell>{t('invoice:createInvoice.memberNr')}</TableCell>
                      <TableCell>{t('invoice:createInvoice.name')}</TableCell>
                      <TableCell align='right'>{t('invoice:createInvoice.amount')}</TableCell>
                      <TableCell align='right'>&nbsp;</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {memberFees.map((item) => {
                      return (
                        <TableRow key={item.memberId}>
                          <TableCell width='30%'>{item.memberNr}</TableCell>
                          <TableCell width='50%'>{`${item.memberFirstName} ${item.memberLastName}`}</TableCell>
                          <TableCell align='right' size='small'>
                            <AmountWithCurrency amount={item.amount} />
                          </TableCell>
                          <TableCell padding='checkbox'>
                            <Tooltip title={t(`invoice:memberFeeTypeEnum.${MemberFeeTypeEnum[item.calculationMethod]}`)}>
                              <span>
                                <IconButton>
                                  <Info fontSize='small' sx={{ color: Colors.ChipColors.Blue }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableHead className='footer'>
                    <TableRow>
                      <TableCell colSpan={2}>&nbsp;</TableCell>
                      <TableCell align='right' size='small' sx={{ whiteSpace: 'nowrap' }}>
                        {t('invoice:createInvoice.sum')}: <AmountWithCurrency amount={memberFees.map((x) => x.amount).reduce((a, b) => a + b)} />
                      </TableCell>
                      <TableCell align='right'>&nbsp;</TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Grid>
    </>
  );
};
